// SAB-592: validate a returnTo target before redirecting through the auth flow.
//
// An open redirect through a login flow is a real vulnerability (phishing: a
// malicious link sends you to a lookalike after you sign in), so we allowlist the
// SHAPE rather than trusting the param: it must be a same-origin RELATIVE path —
// a single leading '/', no protocol, no protocol-relative '//', no backslash
// trick. Anything else resolves to null and the caller falls back to its default.

export function safeReturnToPath(raw: string | null | undefined): string | null {
  if (!raw) return null;
  // Shape allowlist: one leading '/', and the next char is NOT '/' or '\' (which
  // would make it protocol-relative or a known browser backslash-escape).
  if (!/^\/(?![/\\])/.test(raw)) return null;
  try {
    // Resolve against the current origin and require it stays same-origin. Belt to
    // the shape check: catches anything that slips through and escapes the path.
    const u = new URL(raw, window.location.origin);
    if (u.origin !== window.location.origin) return null;
    return u.pathname + u.search + u.hash;
  } catch {
    return null;
  }
}

// SAB-592 / #113: the confirmation-email landing (AuthConfirm) receives its target
// as `next`, which Supabase hands us as an ABSOLUTE url (it's the emailRedirectTo
// we set at signup). Its origin is the one the user signed up on, which can differ
// from the origin serving /auth/confirm — the project Site URL. On staging that
// Site URL is a PINNED preview, so `next` is legitimately cross-origin and a strict
// same-origin check (safeReturnToPath) wrongly rejects it, dropping the invitee on
// the default home.
//
// This is the LAST LINE OF DEFENSE against an open redirect in the signup flow, so
// it does not trust `next`: after verifyOtp the session lives on the CURRENT origin
// and we only ever route inside our own SPA, so we take `next`'s PATH and discard
// its origin entirely. An off-origin `https://evil.com/x` therefore becomes the
// in-app path `/x` on our own origin (never an off-site redirect), and scheme urls
// (javascript:/data:), protocol-relative '//', and backslash tricks resolve to a
// path that fails the leading-'/' / no-'//' guard and return null.
export function safeNextPath(raw: string | null | undefined): string | null {
  if (!raw) return null;
  try {
    const u = new URL(raw, window.location.origin);
    const path = u.pathname + u.search + u.hash;
    if (!path.startsWith('/') || path.startsWith('//')) return null;
    return path;
  } catch {
    return null;
  }
}
